import React, { useEffect } from 'react';
import {
  OutlinedButton,
  TonalButton,
  ButtonType,
  ButtonSize,
} from '@prisjakt/ui/button/variants';
import { EditorToggleButton } from './editor-toggle-button';
import ExperimentContext, { Experiment, SelectedVariants } from './context';
import { EditorContainer, Menu } from './styles';

const fromKebab = (s: string) => s.replace(/-/g, ' ');
const fromCamel = (s: string) => s.replace(/([a-z])([A-Z])/g, '$1 $2');
const unCase = (s: string) => fromCamel(fromKebab(s));

const Editor: React.FC<EditorProps> = ({
  experiments,
  selectedVariants,
  selectedVariant,
}) => {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [experiment, setExperiment] = React.useState<
    Experiment<any> | undefined
  >(undefined);

  useEffect(() => {
    setMounted(true);
  }, []);

  function isActive(exp: Experiment<any>, variant: any) {
    if (!selectedVariants?.[exp.id] && variant === 'baseline') {
      return true;
    }

    if (!selectedVariants?.[exp.id]) {
      return false;
    }

    return selectedVariants[exp.id].selectedVariant === variant;
  }
  const toggleEditor = () => {
    setOpen(!open);
  };

  const resetExperiment = () => {
    setExperiment(undefined);
  };

  if (!mounted) {
    return null;
  }

  return (
    <EditorContainer>
      <EditorToggleButton onClick={toggleEditor} />

      {open && experiment && (
        <Menu>
          <OutlinedButton size={ButtonSize.SMALL} onClick={resetExperiment}>
            {unCase(experiment.id)}
          </OutlinedButton>

          {['baseline']
            .concat(Object.keys(experiment.variants))
            .map(variant => (
              <TonalButton
                size={ButtonSize.SMALL}
                key={variant}
                type={
                  isActive(experiment, variant)
                    ? ButtonType.PRIMARY
                    : ButtonType.TONAL
                }
                onClick={() => {
                  if (selectedVariant) {
                    selectedVariant(experiment, variant);
                  }
                }}
              >
                {unCase(variant)}
              </TonalButton>
            ))}
        </Menu>
      )}
      {open && !experiment && (
        <Menu>
          {experiments?.map(exp => (
            <OutlinedButton
              size={ButtonSize.SMALL}
              key={exp.id}
              onClick={() => {
                setExperiment(exp);
              }}
            >
              {unCase(exp.id)}
            </OutlinedButton>
          ))}
        </Menu>
      )}
    </EditorContainer>
  );
};

interface EditorProps {
  selectedVariants?: SelectedVariants;
  selectedVariant:
    | ((experiment: Experiment<any>, variant: string) => void)
    | undefined;
  experiments?: Array<Experiment<unknown>>;
}

export default () => (
  <ExperimentContext.Consumer>
    {({ selectedVariants, experiments, selectedVariant, showEditor }) => {
      if (!showEditor) {
        return null;
      }
      return (
        <Editor
          selectedVariants={selectedVariants}
          experiments={experiments}
          selectedVariant={selectedVariant}
        />
      );
    }}
  </ExperimentContext.Consumer>
);
